import { Injectable, inject } from '@angular/core';
import { Observable, of, delay, from, BehaviorSubject } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { WarehouseControllerService } from '@app/api/channel-service/api/warehouseController.service';
import { OnlineShopControllerService } from '@app/api/channel-service/api/onlineShopController.service';
import {
  Warehouse as ApiWarehouse,
  WarehouseDTO,
  WarehouseSaveDTO,
  WarehouseListDTO,
  PageRequestDTO,
  PageInfoWarehouseListVO,
  WarehouseListVO,
  ShopDeleteDTO,
} from '@app/api/channel-service/model/models';

import {
  Warehouse,
  WarehousePageResponse,
  WarehouseSearchParams,
  WarehouseCreateRequest,
  WarehouseUpdateRequest,
} from '../models/warehouse.model';

export interface WarehouseOperationResult {
  success: boolean;
  message: string;
  type: 'create' | 'update';
}

@Injectable({
  providedIn: 'root',
})
export class WarehouseService {
  private warehouseOperationResultSubject =
    new BehaviorSubject<WarehouseOperationResult | null>(null);
  public warehouseOperationResult$ =
    this.warehouseOperationResultSubject.asObservable();
  private onlineShopControllerService = inject(OnlineShopControllerService);

  constructor(private warehouseControllerService: WarehouseControllerService) {}

  // Method to emit warehouse operation results
  emitWarehouseOperationResult(result: WarehouseOperationResult): void {
    this.warehouseOperationResultSubject.next(result);
  }

  // Method to clear the last result
  clearWarehouseOperationResult(): void {
    this.warehouseOperationResultSubject.next(null);
  }

  /**
   * Convert API Warehouse to local Warehouse model
   */
  private convertApiWarehouseToLocal(apiWarehouse: ApiWarehouse): Warehouse {
    return {
      id: apiWarehouse.id || 0,
      wareCode: apiWarehouse.wareCode || '',
      wareName: apiWarehouse.wareName,
      contactName: apiWarehouse.contactName || '',
      contactPhone: apiWarehouse.contactPhone || '',
      countryId: apiWarehouse.countryId || 0,
      provinceId: apiWarehouse.provinceId || 0,
      cityId: apiWarehouse.cityId || 0,
      address: apiWarehouse.address || '',
      tenantId: apiWarehouse.tenantId || 1,
      deleteFlag: apiWarehouse.deleteFlag || false,
      status: apiWarehouse.status || false,
      logicWarehouseName: apiWarehouse.logicWarehouseName || '',
      logicFlag: apiWarehouse.logicFlag || false,
      createdName: 'System User', // This might need to be mapped from API response
      creationDate: apiWarehouse.creationDate,
      lastModifiedBy: 'System User',
      lastModifiedDate: apiWarehouse.lastUpdateDate,
    };
  }

  /**
   * Convert local Warehouse to API WarehouseSaveDTO model for creation
   */
  private convertLocalToApiWarehouse(
    warehouse: WarehouseCreateRequest
  ): WarehouseSaveDTO {
    return {
      wareName: warehouse.wareName,
      wareCode: warehouse.wareCode,
      contactName: warehouse.contactName,
      contactPhone: warehouse.contactPhone,
      countryId: warehouse.countryId,
      provinceId: warehouse.provinceId,
      cityId: warehouse.cityId,
      countyId: 0, // Default county ID - might need to be mapped from cityId
      address: warehouse.address,
      status: true,
      logicWarehouseName: warehouse.logicWarehouseName,
      logicFlag: warehouse.logicFlag,
      tenantId: warehouse.tenantId,
      deleteFlag: warehouse.deleteFlag,
    };
  }

  /**
   * Convert local Warehouse to API WarehouseDTO model for update
   */
  private convertLocalToApiWarehouseDTO(
    warehouse: WarehouseUpdateRequest
  ): WarehouseDTO {
    return {
      id: warehouse.id,
      wareCode: warehouse.wareCode,
      wareName: warehouse.wareName,
      contactName: warehouse.contactName,
      contactPhone: warehouse.contactPhone,
      countryId: warehouse.countryId,
      provinceId: warehouse.provinceId,
      cityId: warehouse.cityId,
      countyId: 0, // Default county ID - might need to be mapped from cityId
      address: warehouse.address,
      tenantId: warehouse.tenantId,
      deleteFlag: warehouse.deleteFlag,
      status: warehouse.status,
      logicWarehouseName: warehouse.logicWarehouseName,
      logicFlag: warehouse.logicFlag,
    };
  }

  private mockWarehouses: Warehouse[] = [
    {
      id: 1,
      wareCode: 'WH001',
      wareName: 'Main Warehouse',
      contactName: 'John Smith',
      contactPhone: '13800138001',
      countryId: 1, // Vietnam
      provinceId: 1, // Hà Nội
      cityId: 4, // Phường Ba Đình
      address: '123 Phố Huế',
      tenantId: 1,
      deleteFlag: false,
      status: true,
      logicWarehouseName: 'Main Logic Warehouse',
      logicFlag: true,
      createdName: 'Admin User',
      creationDate: '2024-01-15T10:30:00Z',
    },
    {
      id: 2,
      wareCode: 'WH002',
      wareName: 'Secondary Warehouse',
      contactName: 'Jane Doe',
      contactPhone: '13800138002',
      countryId: 1, // Vietnam
      provinceId: 2, // TP. Hồ Chí Minh
      cityId: 6, // Phường Tân Định
      address: '456 Đường Nguyễn Huệ',
      tenantId: 1,
      deleteFlag: false,
      status: true,
      logicWarehouseName: 'Secondary Logic Warehouse',
      logicFlag: false,
      createdName: 'Manager User',
      creationDate: '2024-01-20T14:15:00Z',
    },
    {
      id: 3,
      wareCode: 'WH003',
      wareName: 'Regional Distribution Center',
      contactName: 'Mike Johnson',
      contactPhone: '13800138003',
      countryId: 1, // Vietnam
      provinceId: 3, // Đà Nẵng
      cityId: 7, // Phường Thạch Thang
      address: '789 Đường 2/9',
      tenantId: 1,
      deleteFlag: false,
      status: false,
      logicWarehouseName: 'Regional Logic Warehouse',
      logicFlag: true,
      createdName: 'Regional Manager',
      creationDate: '2024-02-01T09:45:00Z',
    },
  ];

  private nextId = 4;

  /**
   * Get warehouses with pagination and search parameters
   */
  getWarehouses(
    params: WarehouseSearchParams
  ): Observable<WarehousePageResponse> {
    const warehouseListDTO: WarehouseListDTO = {
      wareName: params.name,
      status: params.enableFlag,
      deleteFlag: params.deleteFlag,
      pageRequest: {
        page: params.page,
        size: params.size,
      },
    };

    return this.warehouseControllerService.pageList(warehouseListDTO).pipe(
      map((response: any) => {
        // Handle blob response
        if (response instanceof Blob) {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const jsonResponse = JSON.parse(reader.result as string);
                resolve(
                  this.processWarehouseResponseData(jsonResponse, params)
                );
              } catch (error) {
                reject(error);
              }
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsText(response);
          });
        }

        return this.processWarehouseResponseData(response, params);
      }),
      switchMap((result: any) => {
        if (result instanceof Promise) {
          return from(result);
        }
        return of(result);
      }),
      catchError((error) => {
        return of({
          content: [],
          totalElements: 0,
          totalPages: 0,
          size: params.size,
          number: params.page,
          first: true,
          last: true,
          numberOfElements: 0,
        });
      })
    );
  }

  private processWarehouseResponseData(
    response: PageInfoWarehouseListVO,
    params: WarehouseSearchParams
  ): WarehousePageResponse {
    const warehousePageResponse: WarehousePageResponse = {
      content:
        response.pageContent?.map((item: WarehouseListVO) => {
          return {
            id: item.id || 0,
            wareCode: item.wareCode || '',
            wareName: item.wareName || '',
            contactName: item.contactName || '',
            contactPhone: item.contactPhone || '',
            countryId: item.countryId || 0,
            provinceId: item.provinceId || 0,
            cityId: item.cityId || 0,
            countyId: item.countyId || 0,
            address: item.address || '',
            tenantId: 1, // Default tenant ID
            deleteFlag: false,
            status: item.status || false,
            logicWarehouseName: '',
            logicFlag: item.status || false, // Use status field for logicFlag
            createdName: item.createdName || 'System User',
            creationDate: item.creationDate || '',
            lastModifiedBy: 'System User',
            lastModifiedDate: '',
          };
        }) || [],
      totalElements: response.totalElements || 0,
      totalPages: response.totalPages || 0,
      size: response.size || params.size,
      number: response.number || params.page,
      first: (response.number || 0) === 0,
      last: (response.number || 0) >= (response.totalPages || 1) - 1,
      numberOfElements: response.numberOfElements || 0,
    };

    return warehousePageResponse;
  }

  /**
   * Get warehouse details by ID
   */
  getWarehouseById(id: number): Observable<Warehouse> {
    return this.warehouseControllerService.getWarehouseDetail(id).pipe(
      map((response: any) => {
        // Handle blob response
        if (response instanceof Blob) {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const jsonResponse = JSON.parse(reader.result as string);
                resolve(this.convertApiWarehouseToLocal(jsonResponse));
              } catch (error) {
                reject(error);
              }
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsText(response);
          });
        }

        return this.convertApiWarehouseToLocal(response);
      }),
      switchMap((result: any) => {
        if (result instanceof Promise) {
          return from(result);
        }
        return of(result);
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  /**
   * Create a new warehouse
   */
  createWarehouse(warehouse: WarehouseCreateRequest): Observable<Warehouse> {
    const apiWarehouse = this.convertLocalToApiWarehouse(warehouse);

    return new Observable<Warehouse>((observer) => {
      this.warehouseControllerService
        .createLabel(apiWarehouse, 'body', false, {
          httpHeaderAccept: '*/*',
        })
        .subscribe({
          next: (response: ApiWarehouse) => {
            const newWarehouse = this.convertApiWarehouseToLocal(response);
            observer.next(newWarehouse);
            observer.complete();
          },
          error: (error: any) => {
            observer.error(error);
          },
        });
    });
  }

  /**
   * Update an existing warehouse
   */
  updateWarehouse(warehouse: WarehouseUpdateRequest): Observable<Warehouse> {
    const apiWarehouseDTO = this.convertLocalToApiWarehouseDTO(warehouse);

    return new Observable<Warehouse>((observer) => {
      this.warehouseControllerService
        .updateWarehouse(apiWarehouseDTO, 'body', false, {
          httpHeaderAccept: '*/*',
        })
        .subscribe({
          next: (response: any) => {
            // Since updateWarehouse returns any, we need to construct the response
            const updatedWarehouse: Warehouse = {
              id: warehouse.id,
              wareCode: warehouse.wareCode,
              wareName: warehouse.wareName,
              contactName: warehouse.contactName,
              contactPhone: warehouse.contactPhone,
              countryId: warehouse.countryId,
              provinceId: warehouse.provinceId,
              cityId: warehouse.cityId,
              address: warehouse.address,
              tenantId: warehouse.tenantId,
              deleteFlag: warehouse.deleteFlag,
              status: warehouse.status,
              logicWarehouseName: warehouse.logicWarehouseName,
              logicFlag: warehouse.logicFlag,
              createdName: 'System User',
              creationDate: '',
              lastModifiedBy: 'System User',
              lastModifiedDate: new Date().toISOString(),
            };
            observer.next(updatedWarehouse);
            observer.complete();
          },
          error: (error: any) => {
            observer.error(error);
          },
        });
    });
  }

  /**
   * Delete a warehouse by ID
   */
  deleteWarehouse(id: number): Observable<void> {
    return new Observable<void>((observer) => {
      this.warehouseControllerService
        .deleteById(id, 'body', false, {
          httpHeaderAccept: '*/*',
        })
        .subscribe({
          next: () => {
            observer.next();
            observer.complete();
          },
          error: (error: any) => {
            observer.error(error);
          },
        });
    });
  }

  /**
   * Batch delete warehouses
   */
  batchDeleteWarehouses(ids: number[]): Observable<void> {
    const shopDeleteDTO: ShopDeleteDTO = {
      ids: ids,
    };

    return new Observable<void>((observer) => {
      this.warehouseControllerService
        .deletes2(shopDeleteDTO, 'body', false, {
          httpHeaderAccept: '*/*',
        })
        .subscribe({
          next: () => {
            observer.next();
            observer.complete();
          },
          error: (error: any) => {
            observer.error(error);
          },
        });
    });
  }

  /**
   * Activate/Deactivate warehouse using operation2 API
   */
  activateDeactivateWarehouse(id: number, flag: boolean): Observable<any> {
    return this.warehouseControllerService.operation2(id, flag).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  /**
   * Update warehouse status (enable/disable) - Legacy method
   */
  updateWarehouseStatus(id: number, status: boolean): Observable<void> {
    // First get the warehouse details, then update with new status
    return new Observable<void>((observer) => {
      this.getWarehouseById(id).subscribe({
        next: (warehouse) => {
          const updateRequest: WarehouseUpdateRequest = {
            id: warehouse.id,
            wareCode: warehouse.wareCode,
            wareName: warehouse.wareName,
            contactName: warehouse.contactName,
            contactPhone: warehouse.contactPhone,
            countryId: warehouse.countryId,
            provinceId: warehouse.provinceId,
            cityId: warehouse.cityId,
            address: warehouse.address,
            tenantId: warehouse.tenantId,
            deleteFlag: warehouse.deleteFlag,
            status: status,
            logicWarehouseName: warehouse.logicWarehouseName,
            logicFlag: warehouse.logicFlag,
          };

          this.updateWarehouse(updateRequest).subscribe({
            next: () => {
              observer.next();
              observer.complete();
            },
            error: (error) => {
              observer.error(error);
            },
          });
        },
        error: (error: any) => {
          observer.error(error);
        },
      });
    });
  }

  /**
   * Check if warehouse IDs are associated with any online shops
   */
  checkWarehouseAssociation(warehouseIds: number[]): Observable<boolean> {
    return this.onlineShopControllerService.checkWarehouseAssociation(
      warehouseIds,
      'body',
      false,
      {
        httpHeaderAccept: '*/*',
      }
    );
  }
}
